// /code/UniqodeInsights/src/browser.ts
import { DeviceIdentifyPlugin } from '@/plugins/DeviceIdentifyPlugin';
import { AutoTrackPlugin, SessionTrackingPlugin } from '@/plugins';
import { UniqodeInsightsClient } from '../core/core';
import { IBasePlugin } from '@/types/plugin';
import { SdkConfig, IUniqodeInsightsClient } from '../types'; // Ensure SdkConfig & IUniqodeInsightsClient are exported from types

// Type for the global SDK object exposed on the window
interface UniqodeInsightsGlobal {
  init: (clientKey: string, userConfig?: Partial<Omit<SdkConfig, 'apiKey'>>) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  track: (eventType: string, eventProperties?: Record<string, any>) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  identify: (userId: string, userProperties?: Record<string, any>) => void;
  flush: () => Promise<void>;
  shutdown: () => Promise<void>;
  addPlugin: (plugin: IBasePlugin) => void; // Allow adding custom plugins
  removePlugin: (pluginName: string) => boolean; // Allow removing custom plugins
  _getClientInstance: () => IUniqodeInsightsClient | null; // For debugging or advanced scenarios
}

let globalSdkInstance: IUniqodeInsightsClient = new UniqodeInsightsClient(); // Initialize with a default instance
const GLOBAL_NAMESPACE = 'UniqodeInsights';

const UniqodeInsightsBrowserApi: UniqodeInsightsGlobal = {
  init: (clientKey: string, userConfig?: Partial<Omit<SdkConfig, 'clientKey'>>) => {
    if (!clientKey || typeof clientKey !== 'string' || clientKey.trim() === '') {
      console.error(
        `${GLOBAL_NAMESPACE}.init() requires a valid apiKey (non-empty string). Initialization aborted.`
      );
      return;
    }

    const fullConfig: SdkConfig = {
      ...(userConfig ?? {}), // Apply user-provided configuration
      clientKey, // Set the apiKey
    };

    try {
      globalSdkInstance.addPlugin(new DeviceIdentifyPlugin());
      globalSdkInstance.addPlugin(new SessionTrackingPlugin());
      globalSdkInstance.addPlugin(new AutoTrackPlugin());
      globalSdkInstance?.identify(undefined, {
        lastSeen: new Date().toISOString(),
        lastSeenURL: window.location.href,
        userAgent: navigator.userAgent,
        screenWidth: window.screen.width,
        screenHeight: window.screen.height,
      });
      globalSdkInstance.init(fullConfig); // Initialize the core client
      console.log(`${GLOBAL_NAMESPACE} SDK initialized successfully.`);
    } catch (error) {
      console.error(`${GLOBAL_NAMESPACE} SDK initialization failed:`, error);
      globalSdkInstance = new UniqodeInsightsClient(); // Ensure instance is reset if init fails
    }
  },

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  track: (eventType: string, eventProperties?: Record<string, any>) => {
    if (!globalSdkInstance) {
      console.warn(
        `${GLOBAL_NAMESPACE} is not initialized. Call .init() first. Event "${eventType}" was not tracked.`
      );
      return;
    }
    try {
      globalSdkInstance.track(eventType, eventProperties);
    } catch (error) {
      console.error(`${GLOBAL_NAMESPACE}.track() failed for event "${eventType}":`, error);
    }
  },

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  identify: (userId: string, userProperties?: Record<string, any>) => {
    if (!globalSdkInstance) {
      console.warn(
        `${GLOBAL_NAMESPACE} is not initialized. Call .init() first. Identify call for user "${userId}" was ignored.`
      );
      return;
    }
    try {
      globalSdkInstance.identify(userId, userProperties);
    } catch (error) {
      console.error(`${GLOBAL_NAMESPACE}.identify() failed for user "${userId}":`, error);
    }
  },

  flush: async (): Promise<void> => {
    if (!globalSdkInstance) {
      console.warn(
        `${GLOBAL_NAMESPACE} is not initialized. Call .init() first. Flush operation was ignored.`
      );
      return Promise.resolve();
    }
    try {
      await globalSdkInstance.flush();
    } catch (error) {
      console.error(`${GLOBAL_NAMESPACE}.flush() failed:`, error);
      // Decide if the error should be re-thrown or just logged
    }
  },

  shutdown: async () => {
    if (!globalSdkInstance) {
      console.warn(`${GLOBAL_NAMESPACE} is not initialized or has already been shut down.`);
      return;
    }
    try {
      await globalSdkInstance.shutdown();
      console.log(`${GLOBAL_NAMESPACE} SDK has been shut down.`);
    } catch (error) {
      console.error(`${GLOBAL_NAMESPACE}.shutdown() failed:`, error);
    } finally {
      globalSdkInstance = null; // Ensure instance is cleared
    }
  },

  _getClientInstance: () => {
    return globalSdkInstance;
  },

  addPlugin: (plugin: IBasePlugin) => {
    if (!globalSdkInstance) {
      console.warn(
        `${GLOBAL_NAMESPACE} is not initialized. Call .init() first. Plugin addition was ignored.`
      );
      return;
    }
    try {
      globalSdkInstance.addPlugin(plugin);
      console.log(`Plugin ${plugin.pluginType} added successfully.`);
    } catch (error) {
      console.error(`Failed to add plugin ${plugin.pluginType}:`, error);
    }
  },
};

// Extend the Window interface to inform TypeScript about the new global property
declare global {
  interface Window {
    [GLOBAL_NAMESPACE]?: UniqodeInsightsGlobal;
  }
}

// Assign the API to the window object if in a browser environment
if (typeof window !== 'undefined') {
  window[GLOBAL_NAMESPACE] = UniqodeInsightsBrowserApi;
}

// Export the API as default for potential module bundler usage if this file is an entry point
// export default UniqodeInsightsBrowserApi;
